<template>
  <div class="tab2">
    <b-overlay :show="loading">
      <div v-if="chartsData">
        <b-card class="mb-4">
          <h6 slot="header">Cân nặng</h6>
          <apexchart
            type="line"
            height="320"
            :options="weightChartOptions"
            :series="weightSeries"
          />
        </b-card>
        <b-card class="mb-4">
          <h6 slot="header">Đường huyết</h6>
          <apexchart
            type="line"
            height="320"
            :options="bloodSugarOptions"
            :series="bloodSugarSeries"
          />
        </b-card>
        <b-card class="mb-4">
          <h6 slot="header">Tiến độ Kế hoạch Hành động</h6>
          <apexchart
            type="bar"
            height="320"
            :options="actionProgressOptions"
            :series="actionProgressSeries"
          />
        </b-card>
      </div>

      <b-card class="mb-4">
        <h6 slot="header">Lịch sử theo dõi</h6>
        <b-table :items="trackingHistory" :fields="trackingFields" responsive small>
          <template #cell(examinationDate)="{ value }">
            {{ value ? $moment.unix(value).utcOffset('+07:00').format('DD/MM/YYYY') : '-' }}
          </template>
          <template #cell(weight)="{ value }">{{ value != null ? value : '-' }}</template>
          <template #cell(waist)="{ value }">{{ value != null ? value : '-' }}</template>
          <template #cell(fastingBloodSugar)="{ value }">{{
            value != null ? value : '-'
          }}</template>
          <template #cell(hba1c)="{ value }">{{ value != null ? value : '-' }}</template>
        </b-table>
        <div v-if="!trackingHistory.length" class="text-center text-muted py-3">
          Chưa có dữ liệu
        </div>
      </b-card>

      <b-card class="mb-4">
        <h6 slot="header">Lịch sử Kế hoạch Hành động</h6>
        <div v-if="actionHistory.length" class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Hành động</th>
                <th v-for="w in actionWeeks" :key="w">Tuần {{ w }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in actionHistory" :key="row.actionName">
                <td>{{ row.actionName }}</td>
                <td v-for="w in actionWeeks" :key="w">
                  <b-badge
                    :variant="statusBadgeVariant(row.weekStatuses && row.weekStatuses[w])"
                  >
                    {{ statusLabel(row.weekStatuses && row.weekStatuses[w]) }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center text-muted py-3">Chưa có dữ liệu</div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { getCharts, getHistory } from '../services/services';

export default {
  name: 'Tab2Progress',
  components: { apexchart: VueApexCharts },
  props: {
    userPackageId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      chartsData: null,
      historyData: null,
      trackingFields: [
        { key: 'weekNumber', label: 'Tuần' },
        { key: 'examinationDate', label: 'Ngày khám' },
        { key: 'weight', label: 'Cân nặng (kg)' },
        { key: 'waist', label: 'Vòng eo (cm)' },
        { key: 'fastingBloodSugar', label: 'ĐH đói' },
        { key: 'hba1c', label: 'HbA1c' },
      ],
    };
  },
  computed: {
    weightChartOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false } },
        dataLabels: { enabled: true },
        xaxis: {
          categories: (this.chartsData?.weightChart || []).map(d => `Tuần ${d.weekNumber}`),
        },
        yaxis: { title: { text: 'kg' } },
      };
    },
    weightSeries() {
      const data = this.chartsData?.weightChart || [];
      return [
        { name: 'Cân nặng thực tế', data: data.map(d => d.actualWeight ?? null) },
        { name: 'Cân nặng dự kiến', data: data.map(d => d.expectedWeight ?? null) },
      ];
    },
    bloodSugarOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false } },
        dataLabels: { enabled: true },
        xaxis: {
          categories: (this.chartsData?.bloodSugarChart || []).map(d => `Tuần ${d.weekNumber}`),
        },
        yaxis: { title: { text: 'mg/dL' } },
      };
    },
    bloodSugarSeries() {
      const data = this.chartsData?.bloodSugarChart || [];
      return [
        { name: 'Đường huyết đói', data: data.map(d => d.fastingBloodSugar ?? null) },
        { name: 'Đường huyết sau ăn', data: data.map(d => d.postprandialBloodSugar ?? null) },
      ];
    },
    actionProgressOptions() {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.chartsData?.actionProgressChart || []).map(d => `Tuần ${d.weekNumber}`),
        },
        yaxis: { max: 100, title: { text: '%' } },
        colors: ['#50cd89', '#f1416c', '#7e8299'],
      };
    },
    actionProgressSeries() {
      const data = this.chartsData?.actionProgressChart || [];
      return [
        { name: 'Hoàn thành tốt', data: data.map(d => d.goodPercent ?? 0) },
        { name: 'Cần cố gắng', data: data.map(d => d.needEffortPercent ?? 0) },
        { name: 'Đang cập nhật', data: data.map(d => d.updatingPercent ?? 0) },
      ];
    },
    trackingHistory() {
      return this.historyData?.trackingHistory || [];
    },
    actionHistory() {
      return (this.historyData?.actionHistory || []).map(row => ({
        actionName: row.action,
        weekStatuses: Object.fromEntries(
          (row.weekStatuses || []).map(ws => [ws.weekNumber, ws.status])
        ),
      }));
    },
    actionWeeks() {
      if (!this.actionHistory.length) return [];
      const allWeeks = new Set();
      this.actionHistory.forEach(row => {
        Object.keys(row.weekStatuses || {}).forEach(w => allWeeks.add(Number(w)));
      });
      return Array.from(allWeeks).sort((a, b) => a - b);
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [chartsRes, historyRes] = await Promise.all([
          getCharts(this.userPackageId),
          getHistory(this.userPackageId),
        ]);
        const charts = chartsRes?.data ?? chartsRes;
        this.chartsData = charts;
        const history = historyRes?.data ?? historyRes;
        this.historyData = history;
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải dữ liệu tiến độ.' });
      } finally {
        this.loading = false;
      }
    },
    statusBadgeVariant(status) {
      if (status === 0) return 'success';
      if (status === 1) return 'danger';
      if (status === 2) return 'secondary';
      return 'light';
    },
    statusLabel(status) {
      if (status === 0) return 'Hoàn thành tốt';
      if (status === 1) return 'Cần cố gắng';
      if (status === 2) return 'Đang cập nhật';
      return '-';
    },
  },
};
</script>

<style scoped>
.tab2 {
  width: 100%;
}

.tab2 ::v-deep .b-overlay-wrap {
  width: 100%;
}

</style>
